
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Phone, Mail, MapPin, Calendar, DollarSign, Shield, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { PushNotification } from '@/components/Notifications/PushNotification';
import { useProfile } from '@/hooks/useProfile';
import { useUserRole } from '@/hooks/useUserRole';
import { usePinAuth } from '@/hooks/usePinAuth';

const Profile = () => {
  const navigate = useNavigate();
  const { pinUser, loading: authLoading } = usePinAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    position: '',
  });

  console.log('Profile component rendered. User:', pinUser?.email, 'Auth Loading:', authLoading);

  const { profile, loading: profileLoading, updateProfile } = useProfile(pinUser?.id);
  const { userRole, loading: roleLoading } = useUserRole();

  console.log('Profile data:', profile);
  console.log('User role:', userRole);
  console.log('Loading states - profile:', profileLoading, 'role:', roleLoading, 'auth:', authLoading);

  React.useEffect(() => {
    if (profile) {
      console.log('Setting form data from profile:', profile);
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        phone: profile.phone || '',
        position: profile.position || '',
      });
    }
  }, [profile]);

  const handleSave = async () => {
    try {
      console.log('Saving profile with data:', formData);
      await updateProfile(formData);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving profile:', error);
    }
  };

  const handleClearCache = async () => {
    setClearingCache(true);
    try {
      // Vider tous les caches du navigateur
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      // Désenregistrer les service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(reg => reg.unregister()));
      }
      // Recharger la page sans cache
      window.location.reload();
    } catch (error) {
      console.error('Erreur lors du vidage du cache:', error);
      // Fallback : recharger quand même
      window.location.reload();
    }
  };

  const handleCancel = () => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        phone: profile.phone || '',
        position: profile.position || '',
      });
    }
    setIsEditing(false);
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'admin':
        return 'destructive';
      case 'manager':
        return 'default';
      case 'employee':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Administrateur';
      case 'manager':
        return 'Manager';
      case 'employee':
        return 'Employé';
      default:
        return 'Non défini';
    }
  };

  // Show loading state while auth is loading
  if (authLoading) {
    console.log('Showing auth loading state');
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 p-4">
        <div className="max-w-md mx-auto pt-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p>Chargement de l'authentification...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if user is not found
  if (!pinUser) {
    console.log('No user found, showing error state');
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 p-4">
        <div className="max-w-md mx-auto pt-8">
          <div className="text-center">
            <h2 className="text-xl font-bold mb-4">Non connecté</h2>
            <p className="text-gray-600 mb-4">Vous devez être connecté pour accéder à votre profil.</p>
            <Button onClick={() => navigate('/auth')}>
              Se connecter
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state while profile data is being fetched
  if (profileLoading || roleLoading) {
    console.log('Showing profile/role loading state');
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 p-4">
        <div className="max-w-md mx-auto pt-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p>Chargement du profil...</p>
          </div>
        </div>
      </div>
    );
  }

  console.log('Rendering full profile page');

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 p-4">
      <div className="max-w-md mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">Mon Profil</h1>
        </div>

        {/* Profile Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Informations personnelles
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">Prénom</Label>
                {isEditing ? (
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  />
                ) : (
                  <div className="text-sm font-medium">{profile?.first_name || 'Non renseigné'}</div>
                )}
              </div>
              <div>
                <Label htmlFor="last_name">Nom</Label>
                {isEditing ? (
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  />
                ) : (
                  <div className="text-sm font-medium">{profile?.last_name || 'Non renseigné'}</div>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email
              </Label>
              <div className="text-sm font-medium text-muted-foreground">{pinUser?.email || profile?.email}</div>
            </div>

            <div>
              <Label htmlFor="phone" className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Téléphone
              </Label>
              {isEditing ? (
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Numéro de téléphone"
                />
              ) : (
                <div className="text-sm font-medium">{profile?.phone || 'Non renseigné'}</div>
              )}
            </div>

            <div>
              <Label htmlFor="position" className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Poste
              </Label>
              {isEditing ? (
                <Input
                  id="position"
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  placeholder="Votre poste"
                />
              ) : (
                <div className="text-sm font-medium">{profile?.position || 'Non renseigné'}</div>
              )}
            </div>

            <div>
              <Label className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Type d'utilisateur
              </Label>
              <Badge variant={getRoleBadgeVariant(userRole?.role || 'employee')}>
                {getRoleLabel(userRole?.role || 'employee')}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Notifications */}
        <PushNotification />

        {/* Work Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Informations professionnelles
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Taux horaire
              </Label>
              <div className="text-sm font-medium">
                {profile?.hourly_rate ? `${profile.hourly_rate}$ / heure` : 'Non défini'}
              </div>
            </div>

            <div>
              <Label>Date d'embauche</Label>
              <div className="text-sm font-medium">
                {profile?.hire_date 
                  ? new Date(profile.hire_date).toLocaleDateString('fr-CA')
                  : 'Non définie'
                }
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button onClick={handleSave} className="flex-1">
                Sauvegarder
              </Button>
              <Button variant="outline" onClick={handleCancel} className="flex-1">
                Annuler
              </Button>
            </>
          ) : (
            <Button onClick={() => setIsEditing(true)} className="w-full">
              Modifier le profil
            </Button>
          )}
        </div>

        {/* Clear Cache */}
        <Button
          variant="outline"
          onClick={handleClearCache}
          disabled={clearingCache}
          className="w-full text-muted-foreground"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${clearingCache ? 'animate-spin' : ''}`} />
          {clearingCache ? 'Vidage en cours...' : 'Vider le cache et recharger'}
        </Button>
      </div>
    </div>
  );
};

export default Profile;
